// frontend/src/App.jsx
import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import OnboardingTour from './components/onboarding/OnboardingTour';
import DemoBanner from './components/onboarding/DemoBanner';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Invoices from './pages/Invoices';
import Tasks from './pages/Tasks';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Payments from './pages/Payments';
import RecurringTasks from './pages/RecurringTasks';
import AdminTenants from './pages/AdminTenants';
import AdminWorkspaces from './pages/AdminWorkspaces';
import Landing from './pages/Landing';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientLayout from './pages/client/ClientLayout';
import Backup from './pages/Backup';
import AuditLogs from './pages/AuditLogs';
import SmsCampaign from './pages/SmsCampaign';
import ClientInvoices from './pages/client/ClientInvoices';
import ClientPayments from './pages/client/ClientPayments';
import ClientClients from './pages/client/ClientClients';
import ClientSuppliers from './pages/client/ClientSuppliers';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ResetPassword from './pages/ResetPassword';
import DigigoCallback from './pages/DigigoCallback';
import Suppliers from './pages/Suppliers';
import PurchaseInvoices from './pages/PurchaseInvoices';
import Quotes from './pages/Quotes';
import CreditNotes from './pages/CreditNotes';
import PurchasePayments from './pages/PurchasePayments';
import BankPage from './pages/BankPage';
import CashPage from './pages/CashPage';
import RSPage from './pages/RSPage';
import PurchaseRSPage from './pages/PurchaseRSPage';
import SupplierStatement from './pages/SupplierStatement';
import FiscalYears from './pages/FiscalYears';
import AccountingEntries from './pages/accounting/AccountingEntries';
import AccountingAR from './pages/accounting/AccountingAR';
import AccountingAP from './pages/accounting/AccountingAP';
import AccountingJournal from './pages/accounting/AccountingJournal';
import ChartOfAccounts from './pages/accounting/ChartOfAccounts'; 

// --- IMPORTS DU CHAT ---
import SupportChat from './support/SupportChat';
import AdminSupport from './pages/AdminSupport';

// ✅ NOUVEAUX IMPORTS COMPTA
import AccountingHome from './pages/accounting/AccountingHome';
import AccountingTreasuryPost from './pages/accounting/AccountingTreasuryPost';
import AccountingSettings from './pages/accounting/AccountingSettings';
import ManualEntry from './pages/accounting/ManualEntry';
import GeneralLedger from './pages/accounting/GeneralLedger';
import TrialBalance from './pages/accounting/TrialBalance';
import FinancialStatements from './pages/accounting/FinancialStatements';
import Annexes from './pages/accounting/Annexes';
import Lettrage from './pages/accounting/Lettrage';

// ✅ IMPORTS STOCK
import StockHome from './pages/stock/StockHome';
import StockItems from './pages/stock/StockItems';
import StockMovements from './pages/stock/StockMovements';
import StockCategories from './pages/stock/StockCategories';
import StockSettings from './pages/stock/StockSettings';
import StockInventory from './pages/stock/StockInventory';

// Layout protégé (Sidebar + Navbar + Contenu)
const ProtectedLayout = ({ children, sidebarOpen, setSidebarOpen }) => {
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <OnboardingTour />
      <div className="flex bg-gray-900 min-h-screen">
        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-50 w-80 transform ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0 transition-transform duration-300 ease-in-out`}
        >
          <Sidebar onNavClick={() => setSidebarOpen(false)} />
        </div>

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Zone contenu */}
        <div className="flex-1 w-full min-h-screen md:ml-80 bg-gray-50 flex flex-col">
          <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
          <DemoBannerWrapper />
          <main className="p-4 md:p-6 w-full flex-grow">{children}</main>
        </div>
      </div>
    </>
  );
};

ProtectedLayout.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  setSidebarOpen: PropTypes.func.isRequired,
};

const DemoBannerWrapper = () => {
  const { currentUser } = useAuth();
  if (!currentUser) return null;
  return <DemoBanner />;
};

// Composant de protection de route
const ProtectedRoute = ({ children, requiredRole, requireSuperAdmin = false }) => {
  const { currentUser, loading, hasRole, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return <div className="flex items-center justify-center h-screen">Chargement...</div>;

  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ProtectedLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
      {children}
    </ProtectedLayout>
  );
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.string,
  requireSuperAdmin: PropTypes.bool,
};

// ✅ Guard Comptabilité (module entitlement)
// Pour cabinets : vérifie le workspace actif. Pour companies : vérifie le tenant.
const AccountingRoute = ({ children }) => {
  const { tenantInfo, loading: authLoading, isCabinet } = useAuth();
  const { activeWorkspace, loading: wsLoading } = useWorkspace();

  if (authLoading || wsLoading) return <div className="flex items-center justify-center h-screen">Chargement...</div>;

  if (isCabinet) {
    const enabled = !!activeWorkspace?.entitlements?.accountingEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  } else {
    const enabled = !!tenantInfo?.entitlements?.accountingEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  }

  return children;
};

AccountingRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// ✅ Guard Stock (module entitlement)
// Pour cabinets : vérifie le workspace actif. Pour companies : vérifie le tenant.
const StockRoute = ({ children }) => {
  const { tenantInfo, loading: authLoading, isCabinet } = useAuth();
  const { activeWorkspace, loading: wsLoading } = useWorkspace();

  if (authLoading || wsLoading) return <div className="flex items-center justify-center h-screen">Chargement...</div>;

  if (isCabinet) {
    const enabled = !!activeWorkspace?.entitlements?.stockEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  } else {
    const enabled = !!tenantInfo?.entitlements?.stockEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  }

  return children;
};

StockRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// ✅ Guard Gestion Cabinet (module entitlement)
// Pour cabinets : vérifie le workspace actif. Pour companies : vérifie le tenant.
const CabinetManagementRoute = ({ children }) => {
  const { tenantInfo, loading: authLoading, isCabinet } = useAuth();
  const { activeWorkspace, loading: wsLoading } = useWorkspace();

  if (authLoading || wsLoading) return <div className="flex items-center justify-center h-screen">Chargement...</div>;

  if (isCabinet) {
    const enabled = !!activeWorkspace?.entitlements?.cabinetManagementEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  } else {
    const enabled = !!tenantInfo?.entitlements?.cabinetManagementEnabled;
    if (!enabled) return <Navigate to="/dashboard" replace />;
  }

  return children;
};

CabinetManagementRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

// ✅ NOUVEAU: Composant wrapper pour les routes
const AppRoutes = () => {
  const { isSuperAdmin } = useAuth();
  const location = useLocation();

  return (
    <>
      <Routes>
        {/* Routes Publiques */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Routes Protégées */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        <Route path="/digigo/callback" element={<DigigoCallback />} />

        <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
        <Route path="/clients/:id" element={<ProtectedRoute><Clients /></ProtectedRoute>} />

        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
        <Route path="/quotes" element={<ProtectedRoute><Quotes /></ProtectedRoute>} />
        <Route path="/credit-notes" element={<ProtectedRoute><CreditNotes /></ProtectedRoute>} />

        <Route path="/tasks" element={<ProtectedRoute><CabinetManagementRoute><Tasks /></CabinetManagementRoute></ProtectedRoute>} />
        <Route path="/recurring-tasks" element={<ProtectedRoute><CabinetManagementRoute><RecurringTasks /></CabinetManagementRoute></ProtectedRoute>} />

        <Route path="/settings" element={<ProtectedRoute requiredRole="admin"><Settings /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute requiredRole="admin"><Users /></ProtectedRoute>} />
        <Route path="/fiscal-years" element={<ProtectedRoute requiredRole="admin"><FiscalYears /></ProtectedRoute>} />

        <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
        <Route path="/purchase-invoices" element={<ProtectedRoute><PurchaseInvoices /></ProtectedRoute>} />
        <Route path="/purchase-payments" element={<ProtectedRoute><PurchasePayments /></ProtectedRoute>} />

        {/* ✅ ROUTES TRÉSORERIE PROTÉGÉES */}
        <Route path="/treasury/bank" element={<ProtectedRoute><BankPage /></ProtectedRoute>} />
        <Route path="/treasury/cash" element={<ProtectedRoute><CashPage /></ProtectedRoute>} />
        <Route path="/treasury/rs" element={<ProtectedRoute><RSPage /></ProtectedRoute>} />
        <Route path="/purchase-payments/rs" element={<ProtectedRoute><PurchaseRSPage /></ProtectedRoute>} />
        <Route path="/suppliers/:id/statement" element={<ProtectedRoute><SupplierStatement /></ProtectedRoute>} />

        {/* ✅ ROUTES COMPTABILITÉ */}
        <Route
          path="/accounting"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingHome />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/treasury"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingTreasuryPost />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <AccountingRoute>
                <AccountingSettings />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/entries"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingEntries />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/journal"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingJournal />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/ar"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingAR />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/ap"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <AccountingAP />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/chart-of-accounts"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <ChartOfAccounts />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/manual-entry"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <ManualEntry />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/general-ledger"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <GeneralLedger />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/trial-balance"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <TrialBalance />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/financial-statements"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <FinancialStatements />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/annexes"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <Annexes />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounting/lettrage"
          element={
            <ProtectedRoute>
              <AccountingRoute>
                <Lettrage />
              </AccountingRoute>
            </ProtectedRoute>
          }
        />

        {/* ✅ ROUTES STOCK */}
        <Route
          path="/stock"
          element={
            <ProtectedRoute>
              <StockRoute>
                <StockHome />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stock/items"
          element={
            <ProtectedRoute>
              <StockRoute>
                <StockItems />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stock/movements"
          element={
            <ProtectedRoute>
              <StockRoute>
                <StockMovements />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stock/categories"
          element={
            <ProtectedRoute>
              <StockRoute>
                <StockCategories />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stock/inventory"
          element={
            <ProtectedRoute>
              <StockRoute>
                <StockInventory />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/stock/settings"
          element={
            <ProtectedRoute requiredRole="admin">
              <StockRoute>
                <StockSettings />
              </StockRoute>
            </ProtectedRoute>
          }
        />

        {/* ROUTE POUR LE TABLEAU DE BORD SUPPORT ADMIN */}
        <Route path="/admin/support" element={<ProtectedRoute requiredRole="admin"><AdminSupport /></ProtectedRoute>} />

        <Route path="/admin/backup" element={<ProtectedRoute requiredRole="admin"><Backup /></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute requiredRole="admin"><AuditLogs /></ProtectedRoute>} />
        <Route path="/sms" element={<ProtectedRoute requiredRole="admin"><SmsCampaign /></ProtectedRoute>} />
        <Route path="/admin/tenants" element={<ProtectedRoute requireSuperAdmin={true}><AdminTenants /></ProtectedRoute>} />
        <Route path="/admin/tenants/:tenantId/workspaces" element={<ProtectedRoute requireSuperAdmin={true}><AdminWorkspaces /></ProtectedRoute>} />

        {/* Routes Portail Client */}
        <Route path="/client/dashboard" element={<ClientLayout><ClientDashboard /></ClientLayout>} />
        <Route path="/client/invoices" element={<ClientLayout><Invoices /></ClientLayout>} />
        <Route path="/client/quotes" element={<ClientLayout><Quotes /></ClientLayout>} />
        <Route path="/client/credit-notes" element={<ClientLayout><CreditNotes /></ClientLayout>} />
        <Route path="/client/payments" element={<ClientLayout><Payments /></ClientLayout>} />
        
        <Route path="/client/treasury/rs" element={<ClientLayout><RSPage /></ClientLayout>} />
        <Route path="/client/purchase-payments/rs" element={<ClientLayout><PurchaseRSPage /></ClientLayout>} />
        
        
        <Route path="/client/purchase-invoices" element={<ClientLayout><PurchaseInvoices /></ClientLayout>} />
        <Route path="/client/purchase-payments" element={<ClientLayout><PurchasePayments /></ClientLayout>} />
        <Route path="/client/treasury/bank" element={<ClientLayout><BankPage /></ClientLayout>} />
        <Route path="/client/treasury/cash" element={<ClientLayout><CashPage /></ClientLayout>} />
        <Route path="/client/settings" element={<ClientLayout><Settings /></ClientLayout>} />
        <Route path="/client/clients" element={<ClientLayout><Clients /></ClientLayout>} />
        <Route path="/client/suppliers" element={<ClientLayout><Suppliers /></ClientLayout>} />

        {/* Route Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* ✅ LE CHAT CLIENT */}
      {!isSuperAdmin && location.pathname !== '/admin/support' && (
        <SupportChat />
      )}
    </>
  );
};

// ✅ COMPOSANT PRINCIPAL AVEC WORKSPACEPROVIDER
export default function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ ENVELOPPER TOUT DANS OnboardingProvider PUIS WorkspaceProvider */}
      <WorkspaceProvider>
        <OnboardingProvider>
          <AppRoutes />
        </OnboardingProvider>
      </WorkspaceProvider>
    </div>
  );
}// force rebuild Sat May 30 00:19:17 CET 2026
